import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import './FAQ.css';

interface FAQListItem {
  title: string;
  text: string;
}

interface FAQBullet {
  label: string;
  text: string;
}

interface FAQSection {
  heading: string;
  text: string;
  subtext?: string;
  bullets: FAQBullet[];
}

interface FAQItem {
  q: string;
  a: string;
  tableHeader?: [string, string];
  table?: [string, string][];
  tableHeader3?: [string, string, string];
  table3?: [string, string, string][];
  note?: string;
  closing?: string;
  list?: FAQListItem[];
  sections?: FAQSection[];
}

interface FAQCategory {
  title: string;
  items: FAQItem[];
}

const renderBold = (text: string) => {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      // biome-ignore lint/suspicious/noArrayIndexKey: parts from split are static content
      <strong key={i}>{part}</strong>
    ) : (
      part
    ),
  );
};

const AccordionItem: React.FC<{ item: FAQItem }> = ({ item }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={`faq-item ${open ? 'open' : ''}`}>
      <button
        type="button"
        className="faq-question"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span>{item.q}</span>
        <svg
          className="faq-chevron"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="faq-answer">
          {item.a.split('\n\n').map((para, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static ordered paragraphs
            <p key={i}>{renderBold(para)}</p>
          ))}
          {item.table && item.tableHeader && (
            <table className="faq-table">
              <thead>
                <tr>
                  <th>{item.tableHeader[0]}</th>
                  <th>{item.tableHeader[1]}</th>
                </tr>
              </thead>
              <tbody>
                {item.table.map((row) => (
                  <tr key={row[0]}>
                    <td>{renderBold(row[0])}</td>
                    <td>{renderBold(row[1])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {item.table3 && item.tableHeader3 && (
            <table className="faq-table faq-table-3col">
              <thead>
                <tr>
                  <th>{item.tableHeader3[0]}</th>
                  <th>{item.tableHeader3[1]}</th>
                  <th>{item.tableHeader3[2]}</th>
                </tr>
              </thead>
              <tbody>
                {item.table3.map((row) => (
                  <tr key={row[0]}>
                    <td>{row[0]}</td>
                    <td>{row[1]}</td>
                    <td className="faq-table-ref">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {item.note && <p className="faq-note">{item.note}</p>}
          {item.list && (
            <ol className="faq-list-numbered">
              {item.list.map((entry) => (
                <li key={entry.title}>
                  <span className="faq-list-title">{entry.title}: </span>
                  {entry.text.split('\n\n').map((part, pi) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: static ordered paragraphs
                    <span key={pi} className={pi > 0 ? 'faq-list-subpara' : undefined}>
                      {renderBold(part)}
                    </span>
                  ))}
                </li>
              ))}
            </ol>
          )}
          {item.sections?.map((section) => (
            <div key={section.heading || section.text} className="faq-section-block">
              {section.heading ? (
                <p className="faq-section-heading">{renderBold(section.heading)}</p>
              ) : null}
              <p className="faq-section-text">{renderBold(section.text)}</p>
              {section.subtext && (
                <p className="faq-section-subtext">{renderBold(section.subtext)}</p>
              )}
              {section.bullets.length > 0 && (
                <ul className="faq-bullets">
                  {section.bullets.map((b) => (
                    <li key={b.label}>
                      <span className="faq-bullet-label">{renderBold(b.label)}: </span>
                      {renderBold(b.text)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
          {item.closing && <p className="faq-closing">{renderBold(item.closing)}</p>}
        </div>
      )}
    </div>
  );
};

const FAQ: React.FC = () => {
  const { t } = useTranslation();

  const categories: FAQCategory[] = [
    {
      title: t('faq.categories.general.title'),
      items: [
        { q: t('faq.categories.general.q1.q'), a: t('faq.categories.general.q1.a') },
        {
          q: t('faq.categories.general.q2.q'),
          a: t('faq.categories.general.q2.a'),
          tableHeader: t('faq.categories.general.q2.tableHeader', { returnObjects: true }) as [
            string,
            string,
          ],
          table: t('faq.categories.general.q2.table', { returnObjects: true }) as [
            string,
            string,
          ][],
        },
        {
          q: t('faq.categories.general.q3.q'),
          a: t('faq.categories.general.q3.a'),
          list: t('faq.categories.general.q3.list', { returnObjects: true }) as FAQListItem[],
        },
        {
          q: t('faq.categories.general.q4.q'),
          a: t('faq.categories.general.q4.a'),
          sections: t('faq.categories.general.q4.sections', {
            returnObjects: true,
          }) as FAQSection[],
        },
      ],
    },
    {
      title: t('faq.categories.methodology.title'),
      items: [
        { q: t('faq.categories.methodology.q1.q'), a: t('faq.categories.methodology.q1.a') },
        {
          q: t('faq.categories.methodology.q2.q'),
          a: t('faq.categories.methodology.q2.a'),
          tableHeader: t('faq.categories.methodology.q2.tableHeader', { returnObjects: true }) as [
            string,
            string,
          ],
          table: t('faq.categories.methodology.q2.table', { returnObjects: true }) as [
            string,
            string,
          ][],
        },
        {
          q: t('faq.categories.methodology.q3.q'),
          a: t('faq.categories.methodology.q3.a'),
          tableHeader3: t('faq.categories.methodology.q3.tableHeader3', {
            returnObjects: true,
          }) as [string, string, string],
          table3: t('faq.categories.methodology.q3.table3', { returnObjects: true }) as [
            string,
            string,
            string,
          ][],
          note: t('faq.categories.methodology.q3.note'),
          closing: t('faq.categories.methodology.q3.closing'),
        },
        { q: t('faq.categories.methodology.q4.q'), a: t('faq.categories.methodology.q4.a') },
        {
          q: t('faq.categories.methodology.q5.q'),
          a: t('faq.categories.methodology.q5.a'),
          list: t('faq.categories.methodology.q5.list', { returnObjects: true }) as FAQListItem[],
        },
        {
          q: t('faq.categories.methodology.q6.q'),
          a: t('faq.categories.methodology.q6.a'),
          list: t('faq.categories.methodology.q6.list', { returnObjects: true }) as FAQListItem[],
        },
        {
          q: t('faq.categories.methodology.q7.q'),
          a: t('faq.categories.methodology.q7.a'),
          list: t('faq.categories.methodology.q7.list', { returnObjects: true }) as FAQListItem[],
        },
        { q: t('faq.categories.methodology.q8.q'), a: t('faq.categories.methodology.q8.a') },
      ],
    },
    {
      title: t('faq.categories.theological.title'),
      items: [
        {
          q: t('faq.categories.theological.q1.q'),
          a: t('faq.categories.theological.q1.a'),
          sections: t('faq.categories.theological.q1.sections', {
            returnObjects: true,
          }) as FAQSection[],
        },
        { q: t('faq.categories.theological.q2.q'), a: t('faq.categories.theological.q2.a') },
        {
          q: t('faq.categories.theological.q3.q'),
          a: t('faq.categories.theological.q3.a'),
          sections: t('faq.categories.theological.q3.sections', {
            returnObjects: true,
          }) as FAQSection[],
          closing: t('faq.categories.theological.q3.closing'),
        },
      ],
    },
    {
      title: t('faq.categories.usage.title'),
      items: [
        {
          q: t('faq.categories.usage.q1.q'),
          a: t('faq.categories.usage.q1.a'),
          tableHeader3: t('faq.categories.usage.q1.tableHeader3', { returnObjects: true }) as [
            string,
            string,
            string,
          ],
          table3: t('faq.categories.usage.q1.table3', { returnObjects: true }) as [
            string,
            string,
            string,
          ][],
        },
        {
          q: t('faq.categories.usage.q2.q'),
          a: t('faq.categories.usage.q2.a'),
          list: t('faq.categories.usage.q2.list', { returnObjects: true }) as FAQListItem[],
          closing: t('faq.categories.usage.q2.closing'),
        },
        {
          q: t('faq.categories.usage.q3.q'),
          a: t('faq.categories.usage.q3.a'),
          list: t('faq.categories.usage.q3.list', { returnObjects: true }) as FAQListItem[],
          closing: t('faq.categories.usage.q3.closing'),
        },
        {
          q: t('faq.categories.usage.q4.q'),
          a: t('faq.categories.usage.q4.a'),
          list: t('faq.categories.usage.q4.list', { returnObjects: true }) as FAQListItem[],
          closing: t('faq.categories.usage.q4.closing'),
        },
      ],
    },
  ];

  return (
    <div className="faq-page">
      <div className="bg-accent-shape" />
      <Header />

      <main className="faq-main">
        <section className="faq-hero">
          <div className="faq-hero-content">
            <div className="hero-line" />
            <h2 className="hero-label">{t('faq.heroLabel', 'Help')}</h2>
            <div className="hero-line" />
          </div>
          <h1 className="hero-title">{t('faq.title', 'Frequently Asked Questions')}</h1>
        </section>

        <section className="faq-content">
          {categories.map((category) => (
            <div key={category.title} className="faq-category">
              <h2 className="faq-category-title">{category.title}</h2>
              <div className="faq-list">
                {category.items.map((item) => (
                  <AccordionItem key={item.q} item={item} />
                ))}
              </div>
            </div>
          ))}
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FAQ;
